'use client';

import CalculatorBase from '@/components/calculator/CalculatorBase';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import IconTextBox, { type IconTextBoxItem } from '@/components/iconTextBox/IconTextBox';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import { RichText } from '@/components/richText/RichText';
import Stage, { type StageProps } from '@/components/stage/Stage';
import StageBenefitsBox from '@/components/stageBenefits/StageBenefits';
import { hasContent } from '@/helper/RichText';
import { ProductCategory, TargetGroup } from '@/helper/enums';
import tracking, { EventCategory, EventSource, type TrackingInformation } from '@/helper/tracking';
import { resolveStory } from '@/helper/utils';
import Awards from '@/storyblok/awards/Awards';
import calculatorClasses from '@/storyblok/calculator/Calculators.module.scss';
import ComponentDownloadsCard from '@/storyblok/componentDownloadsCard/ComponentDownloadsCard';
import type {
  ComponentAwardsStoryblok,
  ComponentDownloadsCardStoryblok,
  EntityProductBenefitStoryblok,
  EntityProductFeatureStoryblok,
  EntityProductStoryblok,
} from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './ProductPage.module.scss';

const ProductPage: React.FC<{ props: EntityProductStoryblok; storyId?: number }> = ({ props, storyId }) => {
  const { t } = useTranslation();
  const [trackingInfo, setTrackingInfo] = useState<TrackingInformation>();

  const stageProps: StageProps = {
    media: props.headerImg,
    type: 'benefits',
  };

  const awards: ComponentAwardsStoryblok = {
    awards: props.certifications ?? [],
    _uid: '',
    component: 'component_awards',
  };

  const iconTextBoxArray: IconTextBoxItem[] = [];

  props.features?.map((item: string | ISbStoryData<EntityProductFeatureStoryblok>) => {
    if (typeof item === 'string') {
      return null;
    }

    iconTextBoxArray.push({
      _uid: item.content._uid,
      color: item.content.color,
      icon: item.content.icon,
      headline: item.content.headline,
      text: item.content.text,
      notes: item.content.notes,
    });
  });
  const downloadCardComponentProps: ComponentDownloadsCardStoryblok = {
    files: [{ files: props.downloads ?? [], _uid: '', component: 'partial_download_category' }],
    title: 'Downloads',
    text: props.page_text ?? { type: 'doc', content: [] },
    size: 'medium',
    component: 'component_downloads_card',
    _uid: '',
  };

  let productCategory = ProductCategory.Electricity;
  if (props.productType === 'gas') {
    productCategory = ProductCategory.Gas;
  }

  useEffect(() => {
    if (!trackingInfo) {
      setTrackingInfo({
        itemName: props.name,
        itemId: props.key,
        itemCategory: EventCategory.Commodity,
        itemCategory2: props.key.toLowerCase().startsWith('s') ? ProductCategory.Electricity : ProductCategory.Gas, //tariff.tp.productCategory ?? '',
        index: 1,
        itemListName: EventSource.TariffDetail,
      });
    } else {
      tracking.productDetail(trackingInfo);
    }
  }, [trackingInfo]);

  return (
    <>
      <Stage
        {...stageProps}
        headlineClassName={classes.productStage}
        productPageCalculator={
          <Suspense>
            <div
              className={classNames(calculatorClasses.calculatorCard, classes.productPageCalculator)}
              data-testid="product-page-calculator-card"
            >
              <HeadlineField
                tag={HeadlineTag.H1}
                style={HeadlineTag.H4}
                className={classNames(classes.productStage, calculatorClasses.calculatorMainTitle)}
              >
                {props.name}
              </HeadlineField>
              <CalculatorBase
                productKey={props.key}
                type={productCategory}
                targetGroup={props.targetGroup === 'gewerbekunde' ? TargetGroup.GK : TargetGroup.PK}
                heatflow={props.zaehlerart_1 === 'Zweitarifzähler'}
                productStoryId={storyId ? String(storyId) : undefined}
              />
            </div>
          </Suspense>
        }
      >
        {props.benefits && props.benefits.length > 0 && (
          <StageBenefitsBox>
            <>
              <HeadlineField tag={HeadlineTag.H3} style={HeadlineTag.H6}>
                {t('productPage.benefitsHeader')}
              </HeadlineField>
              <ul className={classNames('bulletpoints', classes.benefitList)}>
                {props.benefits.map((item, index) => {
                  const benefit = resolveStory<EntityProductBenefitStoryblok>(item)?.content;

                  return (
                    <li key={index} className={classes.listItem}>
                      <RichText text={benefit?.text} />
                      {hasContent(benefit?.info) && (
                        <InfoIconModal
                          headline={benefit?.info_headline}
                          className={classes.infoIcon}
                          trackingLabel={benefit?.info_headline || 'benefit'}
                        >
                          <RichText text={benefit?.info} />
                        </InfoIconModal>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          </StageBenefitsBox>
        )}
      </Stage>
      <div className={classNames(classes.productPage, 'grid-wrapper')}>
        {awards.awards.length !== 0 && (
          <div className={classes.stageAwards}>
            <Awards blok={awards} />
          </div>
        )}
        {!!props.downloads && <ComponentDownloadsCard blok={downloadCardComponentProps} />}
        <IconTextBox
          headlineClassName={classes.features}
          headline={props.features_headline_text}
          items={iconTextBoxArray}
        />
      </div>
    </>
  );
};
export default ProductPage;
